/**
 * useOverdue Hook
 *
 * Provides overdue scheduled transactions for UI display.
 * These are items that are 2+ days past their due date
 * and need user action (Pay, Skip, Ignore).
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getOverdueItems,
  handleOverduePay,
  handleOverdueSkip,
  handleOverdueSkipAndRemove,
  handleOverdueIgnoreMonth,
  handleOverdueIgnorePermanently,
  handleOverdueDismiss,
  OverdueItem,
} from '@services/scheduledRunner';
import { dataEvents } from '@services/dataEvents';

// ============================================================
// Types
// ============================================================

export interface UseOverdueReturn {
  /** List of overdue items */
  items: OverdueItem[];
  /** Loading state */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Total count of overdue items */
  count: number;
  /** Refresh the list */
  refresh: () => Promise<void>;
  /** Pay an overdue item (create transaction) */
  pay: (scheduleId: string, dueDate: Date) => Promise<void>;
  /** Skip an overdue item */
  skip: (scheduleId: string, dueDate: Date, reason?: string) => Promise<void>;
  /** Skip this occurrence AND delete the recurring schedule */
  skipAndRemove: (scheduleId: string, dueDate: Date) => Promise<void>;
  /** Dismiss an item - returns true if escalation popup should show */
  dismiss: (scheduleId: string) => Promise<{ showEscalationPopup: boolean }>;
  /** Ignore for one month */
  ignoreForMonth: (scheduleId: string) => Promise<void>;
  /** Ignore permanently */
  ignorePermanently: (scheduleId: string) => Promise<void>;
}

// ============================================================
// Debug
// ============================================================

const DEBUG =
  import.meta.env.VITE_DEBUG === 'true' ||
  (typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('debug'));

function log(...args: unknown[]): void {
  if (DEBUG) {
    console.log('[useOverdue]', ...args);
  }
}

// ============================================================
// Hook
// ============================================================

export function useOverdue(): UseOverdueReturn {
  const [items, setItems] = useState<OverdueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch overdue items
  const fetchOverdue = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const overdueItems = await getOverdueItems();
      setItems(overdueItems);
      log('[OK] Fetched', overdueItems.length, 'overdue items');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch overdue items';
      console.error('[useOverdue] Error:', err);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch and event subscription
  useEffect(() => {
    fetchOverdue();

    // Subscribe to data change events
    const unsub = dataEvents.on('all', fetchOverdue);
    return () => unsub();
  }, [fetchOverdue]);

  // Pay action
  const pay = useCallback(
    async (scheduleId: string, dueDate: Date) => {
      try {
        await handleOverduePay(scheduleId, dueDate);
        log('[OK] Paid overdue item:', scheduleId);
        // Refresh handled by data events
      } catch (err) {
        console.error('[useOverdue] Pay error:', err);
        throw err;
      }
    },
    []
  );

  // Skip action
  const skip = useCallback(
    async (scheduleId: string, dueDate: Date, reason?: string) => {
      try {
        await handleOverdueSkip(scheduleId, dueDate, reason);
        log('[OK] Skipped overdue item:', scheduleId);
      } catch (err) {
        console.error('[useOverdue] Skip error:', err);
        throw err;
      }
    },
    []
  );

  // Skip & Remove action - skip this occurrence AND delete the recurring schedule
  const skipAndRemove = useCallback(
    async (scheduleId: string, dueDate: Date) => {
      try {
        await handleOverdueSkipAndRemove(scheduleId, dueDate);
        log('[OK] Skip & removed recurring:', scheduleId);
      } catch (err) {
        console.error('[useOverdue] SkipAndRemove error:', err);
        throw err;
      }
    },
    []
  );

  // Dismiss action - tracks dismissCount, shows escalation on 2nd dismiss
  const dismiss = useCallback(
    async (scheduleId: string): Promise<{ showEscalationPopup: boolean }> => {
      try {
        const result = await handleOverdueDismiss(scheduleId);
        log('[OK] Dismissed:', scheduleId, 'showEscalation:', result.showEscalationPopup);
        return result;
      } catch (err) {
        console.error('[useOverdue] Dismiss error:', err);
        throw err;
      }
    },
    []
  );

  // Ignore for month action
  const ignoreForMonth = useCallback(async (scheduleId: string) => {
    try {
      await handleOverdueIgnoreMonth(scheduleId);
      log('[OK] Ignored for month:', scheduleId);
    } catch (err) {
      console.error('[useOverdue] IgnoreForMonth error:', err);
      throw err;
    }
  }, []);

  // Ignore permanently action
  const ignorePermanently = useCallback(async (scheduleId: string) => {
    try {
      await handleOverdueIgnorePermanently(scheduleId);
      log('[OK] Ignored permanently:', scheduleId);
    } catch (err) {
      console.error('[useOverdue] IgnorePermanently error:', err);
      throw err;
    }
  }, []);

  return {
    items,
    loading,
    error,
    count: items.length,
    refresh: fetchOverdue,
    pay,
    skip,
    skipAndRemove,
    dismiss,
    ignoreForMonth,
    ignorePermanently,
  };
}

export default useOverdue;
